/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ButtonLabelLayout } from '../UNSAFE_ButtonLabelLayout';
import { MenuButtonDropdownIcon } from '../PRIVATE_ThemedIcons/MenuButtonDropdownIcon';
import { Menu } from '../UNSAFE_Menu';
import { useMenuAction, MenuToggleDetail } from '../hooks/UNSAFE_useMenuAction';
import { BaseButton } from '../UNSAFE_BaseButton';

import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';

import { ComponentChildren, ComponentChild, Fragment, JSX } from 'preact';
import { forwardRef, ForwardedRef, ElementRef } from 'preact/compat';
import { useRef, useImperativeHandle } from 'preact/hooks';

type ButtonHandle = ElementRef<typeof BaseButton>;

type IntrinsicProps = Pick<
  UnpackSignals<JSX.HTMLAttributes<HTMLElement>>,
  | 'autofocus'
  | 'onBlur'
  | 'onFocus'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'onTouchEnd'
  | 'onTouchStart'
>;

type WidthInterpolationProps = Pick<DimensionProps, 'width'>;

export type Props = IntrinsicProps &
  WidthInterpolationProps &
  TestIdProps & {
    /**
     * children - One or more MenuItems, dividers, or Menu groups
     */
    children?: ComponentChildren;

    /**
     * The icon shown to the left of the label (or to the right of the
     * label in right to left mode). Note that this is not the menu
     * expand icon shown to the right of the label (or to the left of
     * the label in right to left mode).
     */
    icon?: ComponentChild;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * Specifes the button type
     */
    type?: 'button' | 'submit';

    /**
     * The button label
     */
    label?: string;

    /**
     * Specifies if the menu is open
     */
    isMenuOpen?: boolean;

    /**
     * Property that triggers a callback immediately when toggle happens and value of expanded property should be updated
     */
    onToggleMenu?: (details: MenuToggleDetail) => void;

    /**
     * A suffix to the label that can be rendered differently.  Used to identify
     * which child MenuItem is invoked.
     */
    suffix?: string;

    /**
     * Indicates in what states the button has chrome (background and border).
     *     "ghost": "Ghost buttons are the least prominent variant. Ghost buttons are
     * useful for performing low-priority tasks, such as manipulating the UI.",
     *     "borderless"': "Borderless buttons are a more prominent variation. Borderless
     * buttons are useful for supplemental actions that require minimal emphasis.",
     *     "outlined"': "Outlined buttons are salient, but lighter weight than
     * solid buttons. Outlined buttons are useful for secondary actions.",
     *     "solid"': "Solid buttons stand out, and direct the user's attention to the
     *  most important actions in the UI."
     */
    variant?: 'ghost' | 'borderless' | 'outlined' | 'solid';

    /**
     * The size indicates how large the button is rendered: "sm", "md" (default), "lg".
     */
    size?: 'xs' | 'sm' | 'md' | 'lg';

    /**
     * The ID of an element (or space separated IDs of multiple elements) that
     * describes the button.
     */
    'aria-describedby'?: string;
  };

/**
 * A MenuButton is a combined action and menu button.
 */
export const MenuButton = forwardRef(
  (
    {
      children,
      label,
      suffix,
      variant = 'outlined',
      isDisabled = false,
      isMenuOpen = false,
      onToggleMenu,
      size = 'md',
      type: buttonType = 'button',
      width,
      icon,
      testId,
      'aria-describedby': ariaDescribedBy,
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onTouchEnd,
      onTouchStart
    }: Props,
    ref: ForwardedRef<ButtonHandle>
  ) => {
    const buttonRef = useRef<ButtonHandle>(null);
    useImperativeHandle(ref!, () => buttonRef.current!, [buttonRef]);

    const { triggerProps, menuProps } = useMenuAction({
      isDisabled: isDisabled,
      onToggleMenu: onToggleMenu,
      isMenuOpen: isMenuOpen,
      anchorRef: buttonRef
    });

    const elementDetails = {
      type: 'button' as const,
      buttonType: buttonType
    };

    const globalEventProps = {
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onTouchEnd,
      onTouchStart
    };

    const rootButton = (
      <BaseButton
        {...mergeProps(triggerProps, globalEventProps)}
        styling={isMenuOpen ? ['min', 'active'] : ['min']}
        elementDetails={elementDetails}
        isDisabled={isDisabled}
        variant={variant}
        width={width}
        size={size}
        ref={buttonRef}
        testId={testId}
        aria-label={suffix ? `${label} ${suffix}` : label}
        aria-describedby={ariaDescribedBy}>
        <ButtonLabelLayout
          display={'all'}
          suffix={suffix}
          startIcon={icon}
          endIcon={<MenuButtonDropdownIcon />}
          size={size}>
          {label}
        </ButtonLabelLayout>
      </BaseButton>
    );

    /*TODO: Remove this hardcoded offsetValue so we have dropdown offset only set on one single place.
     Could be done via menu props or having a dropdownMenu component. JET-60595*/

    /*TODO: Once menu expose a prop to set min width we would need to calculate offset width of button
      so we set the correct min width since dropdown menus are 200px but if triggered element width is
      bigger should be that size. JET-60745 */

    return isDisabled ? (
      rootButton
    ) : (
      <Fragment>
        {rootButton}
        <Menu {...menuProps} aria-label={label} offsetValue={4}>
          {children}
        </Menu>
      </Fragment>
    );
  }
);

MenuButton.displayName = 'MenuButton';
